import React, { ReactNode, useState, cloneElement, isValidElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Drawer, AppBar, Toolbar, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { MenuIcon } from '../utils/icons';
import '../styles/components/_module-layout.scss';

interface ModuleLayoutProps {
  timeline: ReactNode;
  content: ReactNode;
  actions: ReactNode;
  onTimelineItemClick?: () => void; // Callback to close drawer when item is clicked
}

/**
 * Layout réutilisable pour tous les modules (Quiz, Capsule, Workshop)
 * Structure en 3 colonnes :
 * - Gauche : Timeline de progression
 * - Centre : Contenu du module
 * - Droite : Actions
 */
const ModuleLayout: React.FC<ModuleLayoutProps> = ({ timeline, content, actions, onTimelineItemClick }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // md = 900px
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  // Mobile/Tablette : Drawer pour Timeline
  if (isMobile) {
    return (
      <Box className="module-layout module-layout--mobile">
        {/* AppBar avec bouton hamburger */}
        <AppBar 
          position="static" 
          className="module-layout__appbar"
        >
          <Toolbar className="module-layout__toolbar">
            <IconButton
              className="module-layout__menu-button"
              color="inherit"
              aria-label={t('moduleLayout.menu')}
              edge="start"
              onClick={handleDrawerToggle}
            >
              <MenuIcon size={24} color="#fff" />
            </IconButton>
            {/* Actions à droite */}
            <Box className="module-layout__actions-container">
              {isValidElement(actions)
                ? cloneElement(actions as React.ReactElement<any>, {
                    ...(actions.props as any),
                    isMobile: true // Pass isMobile prop to ActionsSidebar
                  })
                : actions}
            </Box>
          </Toolbar>
        </AppBar>

        {/* Drawer pour Timeline */}
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          className="module-layout__drawer"
        >
          <Box className="module-layout__drawer-content">
            {isValidElement(timeline) && (timeline.props as any).onItemClick
              ? cloneElement(timeline as React.ReactElement<any>, {
                  ...(timeline.props as any),
                  onItemClick: (itemId: string, chapterId: string) => {
                    (timeline.props as any).onItemClick?.(itemId, chapterId);
                    handleDrawerClose(); // Close drawer after clicking an item
                  }
                })
              : timeline}
          </Box>
        </Drawer>

        {/* Colonne centrale - Contenu */}
        <Box className="module-layout__content">
          {content}
        </Box>

        {/* Colonne droite - Actions (masquée sur mobile) */}
        <Box className="module-layout__actions-sidebar--hidden">
          {actions}
        </Box>
      </Box>
    );
  }

  // Desktop : Layout en 3 colonnes
  return (
    <Box className="module-layout module-layout--desktop">
      {/* Colonne gauche - Timeline */}
      <Box className="module-layout__timeline">
        {timeline}
      </Box>

      {/* Colonne centrale - Contenu */}
      <Box className="module-layout__content">
        {content}
      </Box>

      {/* Colonne droite - Actions */}
      <Box className="module-layout__actions-sidebar">
        {actions}
      </Box>
    </Box>
  );
};

export default ModuleLayout;

